// Dispute operations + admin alert console (SM-291 / SM-293 / SM-295).
//
// Everything here talks to backoffice-service, which enforces the
// permissions and proxies the dispute half to market-service. The UI
// never calls market-service directly — that would bypass the audit row
// and the DISPUTE_REVIEW gate.
//
// Money on this surface is the 4dp public wire scale, same as every
// other SPARK amount in the backoffice.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, qs } from "@/lib/api";
import { parseUnitToMinor, unitFromMinor } from "@/lib/currency";

/** Dispute stakes are posted, held and settled in GOLD (SM-291/SM-349). */
const STAKE_CURRENCY = "GOLD" as const;

// ── types ────────────────────────────────────────────────────────────

export type DisputeStatus =
  | "PENDING_FUNDING"
  | "OPEN"
  | "READY_FOR_REVIEW"
  | "ACCEPTED"
  | "REJECTED"
  | "FAILED";

export type StakeSettlement = "HELD" | "REFUNDED" | "FORFEITED" | "FAILED";

/**
 * Server-side queue names. Mirrors apiv1.DisputeQueue*.
 *
 * No longer a tab strip (SM-320) — the page is one list filtered by
 * status. These survive as the keys of the whole-table `counts`, where
 * "overdue" is what keeps an incident visible from a filtered view that
 * excludes it.
 */
export type DisputeQueue = "new" | "waiting" | "ready" | "overdue" | "resolved";

/**
 * One selectable value of the status filter, and equally the value each
 * row's badge shows (SM-320).
 *
 * OVERDUE is not a stored status — it is READY_FOR_REVIEW past its review
 * deadline — but it is what an operator sees on the row, so it has to be
 * filterable as if it were one. Filtering on READY_FOR_REVIEW therefore
 * excludes overdue rows, mirroring market-service: pick one and you never
 * get rows labelled the other, pick both and you get each row once.
 *
 * VOID is the second such value (SM-325). Voiding a market is recorded as
 * an ACCEPTED dispute carrying voided=true — the dispute really was upheld
 * — so the row used to render "Accepted", which reads as the void never
 * having been saved. ACCEPTED now means upheld-and-corrected and VOID is
 * its own value, on the same pick-one/pick-both rule as OVERDUE.
 */
export type DisputeStatusFilter = DisputeStatus | "OVERDUE" | "VOID";

/**
 * The filter menu, in lifecycle order. Deliberately the badge vocabulary
 * rather than the old tab vocabulary: the tabs offered New and Waiting,
 * which were both plain OPEN rows, so two options selected identical
 * results while every row between them read "Open". Resolved was
 * ACCEPTED + REJECTED, which is now two options that can be picked
 * together.
 */
export const DISPUTE_STATUS_FILTERS: DisputeStatusFilter[] = [
  "PENDING_FUNDING",
  "OPEN",
  "READY_FOR_REVIEW",
  "OVERDUE",
  "ACCEPTED",
  "VOID",
  "REJECTED",
  "FAILED",
];

export interface DisputeItem {
  dispute_id: string;
  market_id: string;
  market_question?: string;
  event_id?: string;
  event_title?: string;
  category_id?: string;
  user_id?: string;
  user_email?: string;
  user_nickname?: string;
  status: DisputeStatus;
  evidence?: string;
  /** 4dp minor units. */
  stake_amount: string;
  provisional_outcome_id?: string;
  provisional_outcome_name?: string;
  corrected_outcome_id?: string;
  corrected_outcome_name?: string;
  voided?: boolean;
  decision?: string;
  decided_by?: string;
  decision_notes?: string;
  stake_settlement?: StakeSettlement;
  created_at_ms: number;
  /** Optimistic-concurrency token — echoed back on adjudicate. */
  updated_at_ms: number;
  window_ends_at_ms?: number;
  review_due_at_ms?: number;
  decided_at_ms?: number;
  overdue: boolean;
}

export interface DisputeListResponse {
  items: DisputeItem[];
  page: { limit: number; offset: number; total: number; has_more: boolean };
  counts?: Partial<Record<DisputeQueue, number>>;
}

export interface DisputeTimelineItem {
  kind: string;
  actor_id?: string;
  actor_kind?: string;
  /** Display name for actor_id, filled by backoffice-service for USER
   *  actors — the one kind this app cannot resolve on its own. */
  actor_name?: string;
  message?: string;
  occurred_at_ms: number;
}

export interface DisputeOutcomeItem {
  outcome_id: string;
  code: string;
  name: string;
  is_current: boolean;
}

export interface DisputeDetail {
  dispute: DisputeItem;
  timeline: DisputeTimelineItem[];
  outcomes: DisputeOutcomeItem[];
  /** Blast radius — how many positions/users a decision settles. */
  affected_positions: number;
  affected_users: number;
}

/** Adjudication decisions. Mirrors eventsv1.DisputeDecision*. */
export type DisputeDecision = "REJECT" | "ACCEPT_CORRECT" | "ACCEPT_VOID";

export interface AdjudicateInput {
  decision: DisputeDecision;
  corrected_outcome_id?: string;
  notes?: string;
  /** Optimistic-concurrency token — the dispute's last-known updated_at. */
  expected_updated_at_ms?: number;
}

export interface DisputePolicyItem {
  policy_id: string;
  scope: "GLOBAL" | "CATEGORY" | "EVENT" | "MARKET";
  scope_id?: string;
  scope_name?: string;
  /**
   * The stake currency this row CHOOSES (SM-551) — RED|GREEN|GOLD — or
   * absent when the row leaves the choice to the scope above, the same
   * tri-state every other field here has. It stopped being part of the
   * row's identity in SM-551: a catalog node has exactly one dispute
   * policy, and "which money is the stake" is one of its terms.
   */
  currency?: string;
  /** Tri-state: "true" | "false" | "" (inherit). */
  enabled?: string;
  /** 4dp minor units; "" when inherited. */
  stake_amount?: string;
  window_seconds?: number;
  overdue_seconds?: number;
  updated_by?: string;
  updated_at_ms?: number;
}

/** Scope vocabulary shared by stored rows and per-field provenance. */
export type DisputePolicyScope = "GLOBAL" | "CATEGORY" | "EVENT" | "MARKET";

/**
 * The already-inherited policy for one node: what actually applies
 * after MARKET → EVENT → CATEGORY → GLOBAL resolution.
 *
 * `source_scope` is the source of `enabled` ONLY. Each value field
 * carries its own source, because the row that switched disputes on is
 * usually not the row that set the stake — see the server-side type.
 * An empty per-field source means nothing overrode it and the value is
 * the built-in default.
 */
export interface DisputePolicyView {
  enabled: boolean;
  /** 4dp minor units. */
  stake_amount: string;
  /**
   * The ONE currency this node's dispute is staked in (SM-551): an
   * operator's choice resolved down the scope chain like every other
   * field, the same for every holder whichever book they trade. Not a
   * projection of a trading mode. Absent reads as GOLD.
   */
  stake_currency?: string;
  /**
   * The scope whose row chose `stake_currency` (SM-551), in the same
   * vocabulary as `stake_source`. Absent means no row chose one and the
   * built-in GOLD applies — say "default", never name a row.
   */
  currency_source?: DisputePolicyScope;
  /**
   * Whether anything actually supplied `stake_amount` — a stored row,
   * or the built-in default that exists for GOLD alone.
   *
   * Branch on THIS, never on the amount. A currency nobody configured
   * resolves to `stake_amount: "0"`, and rendering that as a price
   * would tell an operator disputes are free here.
   */
  stake_configured?: boolean;
  window_seconds: number;
  overdue_seconds: number;
  source_scope: DisputePolicyScope | "";
  source_id?: string;
  stake_source?: DisputePolicyScope;
  window_source?: DisputePolicyScope;
  overdue_source?: DisputePolicyScope;
}

/**
 * GET /admin/{markets|events}/{id}/dispute-policy (SM-318).
 *
 * One answer per node (SM-551). A market that opens both books has one
 * dispute policy, so this read no longer takes a book and the currency
 * lives on `policy.stake_currency` with `policy.currency_source`.
 */
export interface EffectiveDisputePolicyResponse {
  scope: "MARKET" | "EVENT";
  target_id: string;
  policy: DisputePolicyView;
  /** The row stored at THIS scope, absent when the node inherits everything. */
  override?: DisputePolicyItem;
  /**
   * DISPUTES_ENABLED, the deployment gate, on its own. `policy.enabled`
   * is the AND of this and the resolved rows, so a false there needs
   * this to be explained: "an operator turned it off for this market"
   * and "this fleet doesn't run disputes" have different fixes.
   */
  feature_enabled: boolean;
}

export interface AlertRouteItem {
  route_id: string;
  event_type: string;
  channel: string;
  enabled: boolean;
  config?: Record<string, string>;
  /** False for channels with no adapter in this build — rendered disabled. */
  available: boolean;
  updated_at_ms?: number;
}

/**
 * Alert configuration health (SM-293).
 *
 * `OK` — deliverable. `DEGRADED` — alerts go out but something is not as
 * intended, or could not be checked. `MISCONFIGURED` — an alert raised
 * right now would reach nobody.
 */
export type AlertHealthStatus = "OK" | "DEGRADED" | "MISCONFIGURED";

export interface AlertChannelHealthItem {
  channel: string;
  status: AlertHealthStatus;
  /** Operator-facing and actionable — safe to render verbatim. */
  detail?: string;
  /** The from-identity that was checked. */
  sender?: string;
  /** False when no enabled route points here, so its status is informational. */
  routed: boolean;
  recipient_count: number;
  /** Resolved recipients the channel rejects before calling the provider. */
  unreachable_recipients?: string[];
}

export interface AlertRouteCoverageItem {
  event_type: string;
  enabled_channels?: string[];
  /** False means the event records no delivery at all — quieter than a failure. */
  routed: boolean;
}

export interface AlertHealthResponse {
  status: AlertHealthStatus;
  channels: AlertChannelHealthItem[];
  coverage: AlertRouteCoverageItem[];
  problems?: string[];
}

export interface AlertDeliveryItem {
  delivery_id: string;
  event_type: string;
  channel: string;
  recipient: string;
  status: "PENDING" | "SENT" | "FAILED" | "DEAD";
  attempts: number;
  dedup_key: string;
  provider_message_id?: string;
  last_error?: string;
  subject?: string;
  dispute_id?: string;
  market_id?: string;
  created_at_ms: number;
  last_attempt_at_ms?: number;
  next_attempt_at_ms?: number;
}

// ── derived helpers ──────────────────────────────────────────────────

/**
 * Badge tone per dispute status — one mapping, used by every surface so
 * a row in the queue and the same row on its detail page can never
 * disagree about how urgent it looks.
 *
 * Overdue outranks status: a READY_FOR_REVIEW dispute past its deadline
 * is an incident (every holder still locked out), not a warning.
 */
export type DisputeTone = "default" | "success" | "warning" | "danger" | "muted" | "accent";

/**
 * The one place a row is turned into a filter value. Everything visible —
 * badge label, badge tone, filter menu, filter summary — is derived from
 * this, so a row can never display a status the filter does not offer, or
 * survive a filter it does not match.
 */
export function disputeFilterOf(
  d: Pick<DisputeItem, "status" | "overdue" | "voided">,
): DisputeStatusFilter {
  if (d.overdue) return "OVERDUE";
  // The void check is deliberately anchored on the stored ACCEPTED status
  // rather than on `voided` alone: `voided` is only ever set by a decision,
  // and reading it on a live row would relabel a dispute nobody has decided.
  if (d.status === "ACCEPTED" && d.voided) return "VOID";
  return d.status;
}

// ── naming a dispute (SM-349) ────────────────────────────────────────
//
// A dispute targets ONE market, and an event can hold many of them —
// "Brazil", "Argentina", … all under "Who wins the World Cup?". Labelling
// a row with a ULID leaves an operator unable to tell which leg is
// contested, since the siblings differ only by that id.
//
// These three are the single derivation for who / which event / which
// market, for the same reason disputeFilterOf() is the single derivation
// for status: the queue, the detail header and the timeline must not
// disagree about what a row is about.
//
// The server fills these names (market-service for the catalog half,
// backoffice-service for the user half). The map arguments are the
// client-side second chance, for when an upstream lookup came back
// empty. Everything falls back to the raw id and never to a blank — an
// operator has no way to tell a missing name from a missing row.

/** The contested market: "Brazil". Falls back to its id. */
export function disputeMarketLabel(
  d: Pick<DisputeItem, "market_id" | "market_question">,
  names?: Map<string, string>,
): string {
  return d.market_question || names?.get(d.market_id) || d.market_id;
}

/**
 * The event the contested market belongs to: "Who wins the World Cup?".
 * Empty when the market hangs directly off a category — a loose market
 * has no event, which is not a missing name, so callers omit the line
 * rather than printing a placeholder.
 */
export function disputeEventLabel(
  d: Pick<DisputeItem, "event_id" | "event_title">,
  names?: Map<string, string>,
): string {
  if (d.event_title) return d.event_title;
  if (!d.event_id) return "";
  return names?.get(d.event_id) || d.event_id;
}

/** Who filed it. Display name, then email, then the raw id. */
export function disputeUserLabel(
  d: Pick<DisputeItem, "user_id" | "user_email" | "user_nickname">,
  names?: Map<string, { display_name?: string; email?: string }>,
): string {
  const u = d.user_id ? names?.get(d.user_id) : undefined;
  return (
    d.user_nickname ||
    d.user_email ||
    u?.display_name ||
    u?.email ||
    d.user_id ||
    "—"
  );
}

export function disputeFilterTone(v: DisputeStatusFilter): DisputeTone {
  switch (v) {
    case "OVERDUE":
      return "danger";
    case "OPEN":
      return "accent";
    case "READY_FOR_REVIEW":
      return "warning";
    case "ACCEPTED":
      return "success";
    // A void is an upheld dispute, but the market has no winner and every
    // holder was refunded — a materially different outcome from a corrected
    // result, so it does not borrow ACCEPTED's green.
    case "VOID":
      return "warning";
    case "REJECTED":
    case "FAILED":
      return "muted";
    default:
      return "default";
  }
}

export function disputeFilterLabel(v: DisputeStatusFilter): string {
  switch (v) {
    case "OVERDUE":
      return "Overdue";
    case "PENDING_FUNDING":
      return "Funding";
    case "OPEN":
      return "Open";
    case "READY_FOR_REVIEW":
      return "Ready for review";
    case "ACCEPTED":
      return "Accepted";
    case "VOID":
      return "Void";
    case "REJECTED":
      return "Rejected";
    case "FAILED":
      return "Failed";
    default:
      return v;
  }
}

/** Short "why is this row here" copy for the filter menu. */
export function disputeFilterHint(v: DisputeStatusFilter): string {
  switch (v) {
    case "PENDING_FUNDING":
      return "Stake not collected yet";
    case "OPEN":
      return "Filed, window still running — decidable now";
    case "READY_FOR_REVIEW":
      return "Window closed — decide now";
    case "OVERDUE":
      return "Past the review deadline; the market is still locked";
    case "ACCEPTED":
      return "Upheld — the winning result was corrected";
    case "VOID":
      return "Upheld by voiding the market; every holder refunded";
    case "REJECTED":
      return "Turned down; the provisional result stands";
    case "FAILED":
      return "Never funded — the market's dispute slot reopened";
    default:
      return "";
  }
}

export function disputeTone(d: Pick<DisputeItem, "status" | "overdue" | "voided">): DisputeTone {
  return disputeFilterTone(disputeFilterOf(d));
}

export function disputeStatusLabel(
  d: Pick<DisputeItem, "status" | "overdue" | "voided">,
): string {
  return disputeFilterLabel(disputeFilterOf(d));
}

/**
 * Whether a dispute can still be decided — i.e. it has not already been
 * decided or failed.
 *
 * An open dispute window does NOT block a decision (SM-295): the
 * remaining time is informational, and an authorized admin can adjudicate
 * from the moment the dispute is submitted. This mirrors the market-service
 * rule; the earlier version of this function also required the window to
 * have closed, which is the behaviour the ticket reversed.
 *
 * `now` is no longer read. It is kept in the signature because callers pass
 * a pinned clock for stable rendering, and because a future rule (an
 * expiry, say) would want it back.
 */
export function isDecidable(d: DisputeItem, _now = Date.now()): boolean {
  return d.status === "OPEN" || d.status === "READY_FOR_REVIEW";
}

/**
 * A policy duration in the compact form the console uses everywhere:
 * "2h", "24h", "7d". Falls back to the next unit down so an odd value
 * (5400s) reads as "90m" rather than a rounded lie.
 */
export function formatPolicySeconds(n: number): string {
  if (!n) return "—";
  if (n % 86400 === 0) return `${n / 86400}d`;
  if (n % 3600 === 0) return `${n / 3600}h`;
  if (n % 60 === 0) return `${n / 60}m`;
  return `${n}s`;
}

/**
 * Where a resolved field came from, in words that name the row to go and
 * edit. An absent source means no override anywhere — the built-in
 * default, which is not a row and must not be described as one.
 */
/**
 * The same provenance in one word, for lines that are scanned rather
 * than read — the publish confirmation above all, where the long phrase
 * doubles the length of a sentence whose job is to show two numbers.
 */
export function policySourceShort(scope: DisputePolicyScope | "" | undefined): string {
  switch (scope) {
    case "MARKET":
      return "market";
    case "EVENT":
      return "event";
    case "CATEGORY":
      return "category";
    case "GLOBAL":
      return "global";
    default:
      return "default";
  }
}

export function policySourceLabel(scope: DisputePolicyScope | "" | undefined): string {
  switch (scope) {
    case "MARKET":
      return "this market's own override";
    case "EVENT":
      return "the event override";
    case "CATEGORY":
      return "the category override";
    case "GLOBAL":
      return "the global policy";
    default:
      return "the built-in default";
  }
}

/** Short form of policySourceLabel, for a badge beside a value. */
export function policySourceBadge(scope: DisputePolicyScope | "" | undefined): string {
  return scope ? scope : "DEFAULT";
}

export type ParsedStake = { ok: true; minor: string } | { ok: false; error: string };

/**
 * A typed SPARK stake → the 4dp minor-unit string the policy API takes.
 *
 * Validated rather than coerced, because every rejection here is a
 * publish the operator would otherwise complete believing a stake was
 * set. The server rejects a stake of zero or less outright (it would
 * make disputes free, removing the only cost of spamming them), so this
 * rejects it too instead of letting the publish fail after the fact.
 *
 * The stake is a GOLD amount, so it converts on GOLD's display unit
 * (SM-460: one displayed unit = wire 10 000). It used to multiply by 100
 * — the retired SPARK-cents lens — while every READ of the same stake
 * renders through SparkAmount on the current unit, so the identical
 * policy read 100x apart between this form and the disputes queue: an
 * operator typing 1000 created a 10.00 stake. Two decimals stays the
 * entry resolution; a third is refused rather than silently truncated.
 */
export function parseStakeToMinor(input: string): ParsedStake {
  const raw = input.trim().replace(/,/g, "");
  if (raw === "") return { ok: false, error: "Enter a dispute stake." };
  if (raw.startsWith("-")) return { ok: false, error: "A dispute stake can't be negative." };
  if (!/^\d+(\.\d+)?$/.test(raw)) {
    return { ok: false, error: "Enter an amount, like 1000 or 15.50." };
  }
  const [, fracRaw = ""] = raw.split(".");
  if (fracRaw.length > 2) {
    return { ok: false, error: "Stakes go down to 0.01 — at most 2 decimal places." };
  }
  const minor = parseUnitToMinor(raw, STAKE_CURRENCY);
  if (!Number.isSafeInteger(minor)) {
    return { ok: false, error: "That stake is too large." };
  }
  if (minor <= 0) {
    return { ok: false, error: "A dispute stake must be greater than zero." };
  }
  return { ok: true, minor: String(minor) };
}

/**
 * The time units a policy duration can be typed in (SM-345).
 *
 * The policy editor takes hours and only hours, which is fine for a page
 * whose whole job is policy. At publish the operator is thinking in the
 * market's own terms — "give people a day", "an hour is enough for this
 * one" — and forcing that through a decimal number of hours is how 30
 * minutes gets typed as 0.5 and read back as half a day.
 *
 * Seconds is included because the wire is integer seconds: without it a
 * window of 90s could be displayed but not re-saved unchanged, and a
 * field that cannot round-trip its own value is a field that quietly
 * rewrites it.
 */
export type DurationUnit = "seconds" | "minutes" | "hours" | "days";

export const DURATION_UNIT_SECONDS: Record<DurationUnit, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
};

export const DURATION_UNIT_OPTIONS: { value: DurationUnit; label: string }[] = [
  { value: "seconds", label: "seconds" },
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
];

export type ParsedDuration = { ok: true; seconds: number } | { ok: false; error: string };

/**
 * A typed duration + unit → the integer seconds the policy API takes.
 *
 * Validated rather than coerced, for the same reason as the stake: every
 * rejection here is a publish the operator would otherwise complete
 * believing a window was set. The server refuses a window of zero or
 * less outright, so this refuses it first, naming the field.
 */
export function parseDurationToSeconds(input: string, unit: DurationUnit): ParsedDuration {
  const raw = input.trim().replace(/,/g, "");
  if (raw === "") return { ok: false, error: "Enter a dispute window." };
  if (raw.startsWith("-")) return { ok: false, error: "A dispute window can't be negative." };
  if (!/^\d+(\.\d+)?$/.test(raw)) {
    return { ok: false, error: `Enter a length in ${unit}, like 24 or 1.5.` };
  }
  // Rounded because the wire is integer seconds and an operator typing
  // 1.5 hours means 5400, not 5399.999…
  const seconds = Math.round(Number(raw) * DURATION_UNIT_SECONDS[unit]);
  if (!Number.isSafeInteger(seconds)) {
    return { ok: false, error: "That dispute window is too long." };
  }
  if (seconds <= 0) {
    return { ok: false, error: "A dispute window must be longer than zero." };
  }
  return { ok: true, seconds };
}

/**
 * Seconds → the largest unit that divides it exactly, so a stored 86400
 * comes back as "1 days" rather than "24 hours" or "1440 minutes". Exact
 * by construction, which is what lets the field be re-saved untouched.
 */
export function secondsToDurationInput(
  seconds: number | undefined,
): { value: string; unit: DurationUnit } {
  if (!seconds || seconds <= 0) return { value: "", unit: "hours" };
  const unit: DurationUnit =
    seconds % 86400 === 0
      ? "days"
      : seconds % 3600 === 0
        ? "hours"
        : seconds % 60 === 0
          ? "minutes"
          : "seconds";
  return { value: String(seconds / DURATION_UNIT_SECONDS[unit]), unit };
}

/** Minor units → the bare SPARK number for an input field: "10000000" → "100000". */
export function stakeToSparkInput(minor: string | undefined): string {
  if (!minor) return "";
  const n = Number(minor);
  if (!Number.isFinite(n)) return "";
  // Exact inverse of parseStakeToMinor, on the same currency unit — the two
  // must agree or the form shows a different number from the one it saved.
  const major = Number(unitFromMinor(n, STAKE_CURRENCY));
  if (!Number.isFinite(major)) return "";
  return Number.isInteger(major) ? String(major) : major.toFixed(2);
}

/** Human countdown to a deadline; null once it has passed. */
export function untilMs(at: number | undefined, now = Date.now()): number | null {
  if (!at) return null;
  const left = at - now;
  return left > 0 ? left : null;
}

export function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ── fetchers ─────────────────────────────────────────────────────────

export interface DisputeListParams {
  /**
   * Comma-separated DisputeStatusFilter values, OR'd server-side. Omit
   * for every status, which is what the queue shows by default.
   */
  status?: string;
  q?: string;
  market_id?: string;
  user_id?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_dir?: string;
}

export const fetchDisputes = (p: DisputeListParams): Promise<DisputeListResponse> =>
  api<DisputeListResponse>(`/admin/disputes${qs(p as Record<string, string | number | undefined>)}`);

// The id reaches these already decoded — the router unescapes $id out of
// the path — so it has to be re-encoded on the way back out, or an id
// carrying a '/' or '%' addresses a different endpoint than the one the
// deep link named (SM-367).
export const fetchDispute = (id: string): Promise<DisputeDetail> =>
  api<DisputeDetail>(`/admin/disputes/${encodeURIComponent(id)}`);

export const adjudicateDispute = (id: string, body: AdjudicateInput) =>
  api<{ dispute: DisputeItem }>(`/admin/disputes/${encodeURIComponent(id)}/adjudicate`, {
    method: "POST",
    body,
  });

/**
 * Every stored row — one per catalog node (SM-551). The page's
 * inheritance preview needs the whole chain, which is why the list is
 * not scoped further here.
 */
export const fetchDisputePolicies = (scope?: string): Promise<{ items: DisputePolicyItem[] }> =>
  api<{ items: DisputePolicyItem[] }>(`/admin/dispute-policies${qs({ scope, limit: 200 })}`);

/**
 * A full replace of the row at scope:scope_id — NOT a patch. An omitted
 * field is cleared, and a body that sets none of the five deletes the
 * row. Senders must always include all five fields.
 */
export interface SetDisputePolicyInput {
  scope: string;
  scope_id?: string;
  /**
   * The stake currency this scope CHOOSES (SM-551): "RED" | "GREEN" |
   * "GOLD", or omitted to inherit it from the scope above. A value like
   * the four below, and part of the full replace — omitting it clears
   * this scope's choice. The server refuses an unknown code rather than
   * defaulting it to GOLD.
   */
  currency?: string;
  /** "" clears the override at this scope (back to inherit). */
  enabled?: string;
  stake_amount?: string;
  window_seconds?: number;
  overdue_seconds?: number;
  /**
   * Optimistic-concurrency token — the `updated_at_ms` of the row that
   * was loaded into the form (SM-324). Omit when creating a row that
   * does not exist yet; send it on every edit, including the clear-all
   * that deletes, so a save built on a stale read is refused with
   * MARKET_000816 instead of overwriting someone else's edit.
   */
  expected_updated_at_ms?: number;
}

export const setDisputePolicy = (body: SetDisputePolicyInput) =>
  api<DisputePolicyItem | null>("/admin/dispute-policies", { method: "PUT", body });

export const fetchMarketDisputePolicy = (
  marketID: string,
): Promise<EffectiveDisputePolicyResponse> =>
  api<EffectiveDisputePolicyResponse>(
    `/admin/markets/${encodeURIComponent(marketID)}/dispute-policy`,
  );

export const fetchEventDisputePolicy = (
  eventID: string,
): Promise<EffectiveDisputePolicyResponse> =>
  api<EffectiveDisputePolicyResponse>(
    `/admin/events/${encodeURIComponent(eventID)}/dispute-policy`,
  );

export const fetchAlertHealth = (): Promise<AlertHealthResponse> =>
  api<AlertHealthResponse>("/admin/alerts/health");

export const fetchAlertRoutes = (): Promise<{ items: AlertRouteItem[] }> =>
  api<{ items: AlertRouteItem[] }>("/admin/alerts/routes");

export const updateAlertRoute = (
  eventType: string,
  channel: string,
  body: { enabled: boolean; config?: Record<string, string> },
) =>
  api<AlertRouteItem>(`/admin/alerts/routes/${eventType}/${channel}`, {
    method: "PUT",
    body,
  });

export const fetchAlertDeliveries = (p: {
  event_type?: string;
  channel?: string;
  status?: string;
  dispute_id?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: AlertDeliveryItem[]; page: { total: number } }> =>
  api(`/admin/alerts/deliveries${qs(p as Record<string, string | number | undefined>)}`);

export const sendTestAlert = (channel: string, recipient?: string) =>
  api<{ delivery: AlertDeliveryItem }>("/admin/alerts/test", {
    method: "POST",
    body: { channel, recipient },
  });

// ── hooks ────────────────────────────────────────────────────────────

export const disputeKeys = {
  list: (p: DisputeListParams) => ["disputes", p] as const,
  detail: (id: string) => ["dispute", id] as const,
  policies: (scope?: string) => ["dispute-policies", scope ?? "all"] as const,
  // One entry per node (SM-551): a market has one dispute policy
  // whichever books it trades, so there is no mode to key by.
  effectivePolicy: (scope: "MARKET" | "EVENT", id: string) =>
    ["dispute-policy-effective", scope, id] as const,
  alertRoutes: () => ["alert-routes"] as const,
  alertHealth: () => ["alert-health"] as const,
  alertDeliveries: (p: unknown) => ["alert-deliveries", p] as const,
};

/**
 * Queue list. Polls on a 30s cadence: disputes arrive on their own
 * schedule and an operator sitting on the queue should see one land
 * without reloading — but the volume is low enough that anything tighter
 * is pure noise.
 */
export function useDisputes(p: DisputeListParams) {
  return useQuery({
    queryKey: disputeKeys.list(p),
    queryFn: () => fetchDisputes(p),
    refetchInterval: 30_000,
  });
}

export function useDispute(id: string | undefined) {
  return useQuery({
    queryKey: disputeKeys.detail(id ?? ""),
    queryFn: () => fetchDispute(id!),
    enabled: Boolean(id),
  });
}

export function useAdjudicate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdjudicateInput) => adjudicateDispute(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: disputeKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: ["disputes"] });
    },
  });
}

export function useDisputePolicies(scope?: string) {
  return useQuery({
    queryKey: disputeKeys.policies(scope),
    queryFn: () => fetchDisputePolicies(scope),
  });
}

export function useSetDisputePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setDisputePolicy,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dispute-policies"] });
      // A stored row changes what every node below it resolves to, and
      // the resolved reads are cached per node — leaving them would show
      // a market the terms it had before the override was written.
      void qc.invalidateQueries({ queryKey: ["dispute-policy-effective"] });
    },
  });
}

/**
 * The resolved dispute terms for one catalog node.
 *
 * `staleTime: 0` for the same reason the commission lookup uses it: a
 * category or event override may have moved since this node was last
 * opened, and this number is shown next to a button that commits to it.
 */
export function useEffectiveDisputePolicy(opts: {
  scope: "MARKET" | "EVENT";
  id: string;
  enabled?: boolean;
}) {
  const { scope, id, enabled = true } = opts;
  return useQuery({
    queryKey: disputeKeys.effectivePolicy(scope, id),
    queryFn: () =>
      scope === "MARKET" ? fetchMarketDisputePolicy(id) : fetchEventDisputePolicy(id),
    enabled: enabled && Boolean(id),
    staleTime: 0,
  });
}

export function useAlertRoutes() {
  return useQuery({ queryKey: disputeKeys.alertRoutes(), queryFn: fetchAlertRoutes });
}

/**
 * Alert configuration health. The server checks the sender domain
 * against the provider, so this is a real network call — refetching on
 * focus would hammer it for no benefit, and the answer only changes when
 * someone changes configuration.
 */
export function useAlertHealth() {
  return useQuery({
    queryKey: disputeKeys.alertHealth(),
    queryFn: fetchAlertHealth,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export function useUpdateAlertRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventType,
      channel,
      enabled,
      config,
    }: {
      eventType: string;
      channel: string;
      enabled: boolean;
      config?: Record<string, string>;
    }) => updateAlertRoute(eventType, channel, { enabled, config }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: disputeKeys.alertRoutes() });
      // Toggling a route changes coverage — disabling the last enabled
      // channel for an event is exactly the silent misconfiguration the
      // health panel exists to catch, so it must not stay stale here.
      void qc.invalidateQueries({ queryKey: disputeKeys.alertHealth() });
    },
  });
}

export function useAlertDeliveries(p: Parameters<typeof fetchAlertDeliveries>[0]) {
  return useQuery({
    queryKey: disputeKeys.alertDeliveries(p),
    queryFn: () => fetchAlertDeliveries(p),
    refetchInterval: 30_000,
  });
}

export function useSendTestAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channel, recipient }: { channel: string; recipient?: string }) =>
      sendTestAlert(channel, recipient),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["alert-deliveries"] }),
  });
}
